// M7.1 — /app/exports. Three export types:
//   1. Magazine PDF — async DocRaptor flow with polling + SparkPost email
//   2. Data bundle — immediate client-side multi-sheet XLSX download
//   3. Image pack — disabled, ships in a later milestone
//
// The PDF flow hits POST /api/export/pdf → polls /api/export/jobs/:id
// every 3s → surfaces a stepper until the job lands or fails.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { CtaPlacement } from "@/components/CtaPlacement";
import { reportHandledError } from "@/lib/bugsnag";
import { pushGtmEvent } from "@/lib/gtm-events";
import { cn } from "@/lib/utils";
import { downloadXlsx } from "@/lib/xlsx";
import { formatDateRangeLabel } from "@shared/report/date-range";
import {
  fetchBootstrap,
  fetchReport,
  revokeExportDownload,
  type BootstrapResponse,
  type ReportCompare,
  type ReportPayload,
  type ReportRange,
} from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

type Status =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "no-property" }
  | { kind: "error"; message: string }
  | { kind: "ready"; bootstrap: BootstrapResponse; report: ReportPayload };

interface PdfJob {
  jobId: string;
  status: "queued" | "rendering" | "uploading" | "ready" | "failed";
  downloadUrl?: string;
  /** The bearer key embedded in downloadUrl — surfaced so the client can
   *  call DELETE /api/export/download/:key without re-parsing the URL. */
  downloadKey?: string;
  error?: string;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_RANGES: ReadonlyArray<ReportRange> = ["last_7d", "last_28d", "last_90d"];
function isIsoDate(v: string | null): v is string {
  return v !== null && ISO_DATE_RE.test(v);
}
function isRange(v: string | null): v is ReportRange {
  return v !== null && (VALID_RANGES as readonly string[]).includes(v);
}

// M13.5 — `custom` is included so the Exports header button can forward
// the dashboard's custom-compare bounds. When custom is requested but
// the bounds are missing/invalid the parser below downgrades to
// "previous" so /api/report doesn't 400 on a half-formed request.
const VALID_COMPARES: ReadonlyArray<ReportCompare> = [
  "previous",
  "year_over_year",
  "none",
  "custom",
];
function isCompare(v: string | null): v is ReportCompare {
  return v !== null && (VALID_COMPARES as readonly string[]).includes(v);
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function default28d(): { startDate: string; endDate: string } {
  const today = new Date();
  const end = new Date(today);
  end.setDate(today.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

/**
 * Pick the closest preset enum for a bounds window. Used when
 * submitting an export job — the server schema still requires a
 * preset label alongside the bounds for the outgoing email + telemetry
 * tag. Falls back to last_28d for custom windows that don't snap to a
 * preset day-count.
 */
function derivePresetFromBounds(startDate: string, endDate: string): ReportRange {
  const msPerDay = 24 * 60 * 60 * 1000;
  const startMs = new Date(`${startDate}T00:00:00Z`).getTime();
  const endMs = new Date(`${endDate}T00:00:00Z`).getTime();
  const days = Math.round((endMs - startMs) / msPerDay) + 1;
  if (days === 7) return "last_7d";
  if (days === 90) return "last_90d";
  return "last_28d";
}

export function ExportsPage() {
  usePageMeta({ title: "Exports · Traffic Snapshot" });
  const [params] = useSearchParams();
  // M12.2 — bounds-first URL. Legacy `?range=` still resolves (so old
  // share/bookmark URLs keep working) but we always normalise to bounds
  // before forwarding to the export job API.
  const fallback = useMemo(() => default28d(), []);
  const rawStart = params.get("start");
  const rawEnd = params.get("end");
  const rawRange = params.get("range");
  const useBounds = isIsoDate(rawStart) && isIsoDate(rawEnd);
  const startDate = useBounds ? (rawStart as string) : fallback.startDate;
  const endDate = useBounds ? (rawEnd as string) : fallback.endDate;
  const legacyRange: ReportRange | null = !useBounds && isRange(rawRange) ? rawRange : null;
  // Validate against the allowlist so a stray ?compare=foo doesn't
  // propagate into /api/report or /api/export/pdf (both would 400 on
  // the bad value while the UI keeps treating it as valid).
  const requestedCompare: ReportCompare = isCompare(params.get("compare"))
    ? (params.get("compare") as ReportCompare)
    : "previous";
  // M13.5 — `Exports` button on ReportOverview forwards compareStart /
  // compareEnd when compare === "custom" so the export job mirrors the
  // dashboard's selection. If custom is requested but the bounds are
  // missing/invalid, downgrade to "previous" — sending compare=custom
  // without bounds 400s /api/report.
  const rawCompareStart = params.get("compareStart");
  const rawCompareEnd = params.get("compareEnd");
  const hasValidCustomCompareBounds = isIsoDate(rawCompareStart) && isIsoDate(rawCompareEnd);
  const compare: ReportCompare =
    requestedCompare === "custom" && !hasValidCustomCompareBounds ? "previous" : requestedCompare;
  const compareStart = compare === "custom" ? (rawCompareStart as string) : undefined;
  const compareEnd = compare === "custom" ? (rawCompareEnd as string) : undefined;

  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let bootstrap: BootstrapResponse | null = null;
      try {
        bootstrap = await fetchBootstrap();
        if (cancelled) return;
        if (!bootstrap.authenticated) return setStatus({ kind: "unauthenticated" });
        if (!bootstrap.defaultProperty) return setStatus({ kind: "no-property" });
        const report = await fetchReport(
          legacyRange
            ? {
                propertyId: bootstrap.defaultProperty.propertyId,
                range: legacyRange,
                compare,
              }
            : {
                propertyId: bootstrap.defaultProperty.propertyId,
                startDate,
                endDate,
                compare,
                ...(compareStart ? { compareStart } : {}),
                ...(compareEnd ? { compareEnd } : {}),
              },
        );
        if (cancelled) return;
        setStatus({ kind: "ready", bootstrap, report });
      } catch (err) {
        if (cancelled) return;
        // Only treat the bootstrap failure as "unauthenticated". Anything
        // thrown after bootstrap (eg. /api/report outage) is a real error
        // we should surface, not a silent redirect to Connect.
        if (!bootstrap) {
          setStatus({ kind: "unauthenticated" });
        } else {
          setStatus({ kind: "error", message: (err as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, legacyRange, compare, compareStart, compareEnd]);

  if (status.kind === "unauthenticated") {
    return <Navigate to="/app/connect?return_to=%2Fapp%2Fexports" replace />;
  }

  const property = status.kind === "ready" ? status.bootstrap.defaultProperty : null;
  const report = status.kind === "ready" ? status.report : null;
  const propertyLabel = property?.propertyName ?? "report";

  return (
    <AppShell
      property={property ?? null}
      eyebrow="Exports"
      title={
        <>
          Take your snapshot <em className="font-serif text-purple-700">offline</em>
        </>
      }
      subtitle="Magazine-style PDF, data bundle, or image pack — all scoped to the same period as Traffic report."
    >
      {status.kind === "loading" ? (
        <p className="text-sm text-neutral-600">Loading your report…</p>
      ) : status.kind === "error" ? (
        <div className="rounded-2xl bg-white p-8 ring-1 ring-red-200">
          <p className="font-display text-base font-semibold text-neutral-950">
            Couldn't load the report
          </p>
          <p className="mt-2 max-w-[60ch] text-sm text-neutral-600 text-pretty">
            {status.message || "We hit an unexpected error fetching your report."}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        </div>
      ) : status.kind === "no-property" ? (
        <div className="rounded-2xl bg-white p-8 ring-1 ring-neutral-950/5">
          <p className="font-display text-base font-semibold text-neutral-950">
            Pick a property first
          </p>
          <p className="mt-2 max-w-[60ch] text-sm text-neutral-600 text-pretty">
            Connect a GA4 property so we know what to export.
          </p>
          <Button asChild className="mt-4">
            <Link to="/app/properties">Choose property</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          <PdfCard
            propertyId={property?.propertyId ?? ""}
            propertyName={propertyLabel}
            startDate={startDate}
            endDate={endDate}
            legacyRange={legacyRange}
            compare={compare}
            compareStart={compareStart}
            compareEnd={compareEnd}
            userEmail={status.bootstrap.user?.email ?? ""}
          />
          <DataBundleCard
            report={report!}
            propertyLabel={propertyLabel}
            startDate={startDate}
            endDate={endDate}
          />
          <ComingSoonCard
            title="Image pack"
            description="PNGs of every section, ready for decks and Notion."
            icon="fa-regular fa-image"
          />
        </div>
      )}
    </AppShell>
  );
}

function ExportCardShell({
  icon,
  title,
  description,
  children,
  tone = "default",
}: {
  icon: string;
  title: string;
  description: string;
  children: React.ReactNode;
  tone?: "default" | "muted";
}) {
  return (
    <article
      className={cn(
        "flex flex-col rounded-2xl p-6 ring-1",
        tone === "muted" ? "bg-neutral-50 ring-neutral-200" : "bg-white ring-neutral-950/5",
      )}
    >
      <div
        className={cn(
          "inline-flex size-10 items-center justify-center rounded-full ring-1",
          tone === "muted"
            ? "bg-white text-neutral-400 ring-neutral-200"
            : "bg-purple-50 text-purple-700 ring-purple-100",
        )}
      >
        <i className={`${icon} text-lg`} aria-hidden="true" />
      </div>
      <h3 className="mt-4 font-display text-base font-semibold text-neutral-950">{title}</h3>
      <p className="mt-1 text-sm text-neutral-600 text-pretty">{description}</p>
      <div className="mt-auto pt-4">{children}</div>
    </article>
  );
}

interface PdfCardProps {
  propertyId: string;
  propertyName: string;
  startDate: string;
  endDate: string;
  /** Preset range the caller parsed from a legacy URL (`?range=last_28d`).
   *  When set, we forward the preset to the export API unchanged so
   *  pre-existing bookmarks keep the same behaviour. Otherwise we send
   *  startDate/endDate + derive a preset enum for backward-compat. */
  legacyRange: ReportRange | null;
  compare: ReportCompare;
  /** SKE-3473 follow-up — custom compare bounds (only defined when
   *  `compare === "custom"`) so the export POST body can forward them
   *  and the queue consumer's cache keys line up with the dashboard's. */
  compareStart: string | undefined;
  compareEnd: string | undefined;
  userEmail: string;
}

function PdfCard({
  propertyId,
  propertyName,
  startDate,
  endDate,
  legacyRange,
  compare,
  compareStart,
  compareEnd,
  userEmail,
}: PdfCardProps) {
  const [job, setJob] = useState<PdfJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [revokingDownload, setRevokingDownload] = useState(false);
  const pollRef = useRef<number | null>(null);
  // Track unmount so an in-flight POST that resolves *after* unmount
  // doesn't leak a setInterval or call setState on a dead component.
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  async function generate() {
    if (!propertyId) return;
    setSubmitting(true);
    setJob(null);
    try {
      // Export POST schema still requires a preset `range` for legacy
      // code paths (email subject line, analytics). We derive it from
      // the bounds-based window and also send start/end so the consumer
      // uses the exact window the user picked. When a legacy `?range=`
      // URL is open we omit bounds so the consumer resolves the preset
      // fresh — otherwise a stale `last_28d` default would override the
      // user's chosen preset.
      const derivedRange: ReportRange = legacyRange ?? derivePresetFromBounds(startDate, endDate);
      const res = await fetch("/api/export/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          propertyId,
          range: derivedRange,
          compare,
          ...(legacyRange ? {} : { startDate, endDate }),
          // SKE-3473 follow-up — forward the custom compare bounds so
          // the queue consumer's cache keys line up with the dashboard's
          // and the warm-cache short-circuit fires. Only sent when the
          // user is actually on a custom compare; preset compares omit
          // the fields entirely (the server refuses stray bounds).
          ...(compare === "custom" && compareStart && compareEnd
            ? { compareStart, compareEnd }
            : {}),
        }),
      });
      if (!mountedRef.current) return;
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `Failed (${res.status})` }));
        if (!mountedRef.current) return;
        setJob({
          jobId: "",
          status: "failed",
          error: (body as { error?: string }).error ?? "Couldn't start render",
        });
        return;
      }
      const body = (await res.json()) as { jobId: string };
      if (!mountedRef.current) return;
      setJob({ jobId: body.jobId, status: "queued" });
      pushGtmEvent("export_pdf_requested", {
        property_id: propertyId,
        range: derivedRange,
        compare,
        start_date: startDate,
        end_date: endDate,
      });
      pollRef.current = window.setInterval(() => pollJob(body.jobId), 3000);
    } catch (err) {
      if (!mountedRef.current) return;
      setJob({ jobId: "", status: "failed", error: (err as Error).message });
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  async function pollJob(id: string) {
    try {
      const res = await fetch(`/api/export/jobs/${id}`, { credentials: "include" });
      if (!mountedRef.current || !res.ok) return;
      const body = (await res.json()) as PdfJob;
      if (!mountedRef.current) return;
      setJob((prev) => (prev ? { ...prev, ...body } : body));
      if (body.status === "ready" || body.status === "failed") stopPolling();
    } catch {
      /* transient — keep polling */
    }
  }

  return (
    <ExportCardShell
      icon="fa-regular fa-file-pdf"
      title="Magazine PDF"
      description="Full-bleed cover, typographic sections, and a recommendations page. Rendered server-side — we email you the link."
    >
      {userEmail ? (
        <p className="mb-3 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 ring-1 ring-neutral-200">
          We'll email the link to{" "}
          <strong className="font-semibold text-neutral-950">{userEmail}</strong> — the Google
          account you signed in with.
        </p>
      ) : null}
      {!job ? (
        <Button
          type="button"
          onClick={generate}
          disabled={submitting || !propertyId}
          className="ts-cta"
        >
          {submitting ? "Starting…" : "Generate PDF"}
          <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
        </Button>
      ) : job.status === "ready" && job.downloadUrl ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild>
                <a href={job.downloadUrl} target="_blank" rel="noopener noreferrer">
                  Download PDF
                  <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
                </a>
              </Button>
              {job.downloadKey ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={revokingDownload}
                  onClick={async () => {
                    if (!job.downloadKey) return;
                    setRevokingDownload(true);
                    try {
                      await revokeExportDownload(job.downloadKey);
                      setJob((prev) =>
                        prev ? { ...prev, downloadUrl: undefined, downloadKey: undefined } : prev,
                      );
                    } catch (err) {
                      console.error("revokeExportDownload failed:", err);
                      reportHandledError(err, {
                        source: "ExportsPage.revokeExportDownload",
                      });
                    } finally {
                      setRevokingDownload(false);
                    }
                  }}
                >
                  {revokingDownload ? "Deleting…" : "Delete link"}
                </Button>
              ) : null}
            </div>
            {userEmail ? (
              <p className="text-xs text-neutral-500">Emailed a copy to {userEmail}.</p>
            ) : null}
            <p className="text-xs text-neutral-500">
              Link expires in 48 hours. Delete it immediately if sent to the wrong address.
            </p>
          </div>
          <CtaPlacement context={{ route: "/app/exports", signals: { hasExport: true } }} />
        </div>
      ) : job.status === "failed" ? (
        <div className="space-y-2">
          <p className="text-sm text-red-700">{job.error ?? "Something went wrong."}</p>
          <Button type="button" variant="outline" onClick={generate}>
            Try again
          </Button>
        </div>
      ) : (
        <JobStepper job={job} propertyName={propertyName} />
      )}
    </ExportCardShell>
  );
}

function JobStepper({ job, propertyName }: { job: PdfJob; propertyName: string }) {
  const steps: Array<{ key: PdfJob["status"]; label: string }> = [
    { key: "queued", label: "Queued" },
    { key: "rendering", label: "Rendering" },
    { key: "uploading", label: "Finalising" },
    { key: "ready", label: "Ready" },
  ];
  const activeIdx = steps.findIndex((s) => s.key === job.status);
  return (
    <div>
      <p className="text-xs text-neutral-500">Rendering {propertyName}…</p>
      <ul role="list" className="mt-3 space-y-1.5">
        {steps.map((step, idx) => {
          const done = idx < activeIdx;
          const current = idx === activeIdx;
          return (
            <li
              key={step.key}
              className={cn(
                "flex items-center gap-2 text-xs",
                done ? "text-green-700" : current ? "text-purple-700" : "text-neutral-400",
              )}
            >
              <span
                className={cn(
                  "inline-flex size-4 items-center justify-center rounded-full",
                  done ? "bg-green-100" : current ? "bg-purple-100" : "bg-neutral-100",
                )}
              >
                {done ? (
                  <i className="fa-solid fa-check text-[8px]" aria-hidden="true" />
                ) : current ? (
                  <i className="fa-solid fa-circle-notch fa-spin text-[8px]" aria-hidden="true" />
                ) : (
                  <i className="fa-regular fa-circle text-[8px]" aria-hidden="true" />
                )}
              </span>
              <span>{step.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface DataBundleCardProps {
  report: ReportPayload;
  propertyLabel: string;
  startDate: string;
  endDate: string;
}

function DataBundleCard({ report, propertyLabel, startDate, endDate }: DataBundleCardProps) {
  const [downloading, setDownloading] = useState(false);

  async function download() {
    setDownloading(true);
    try {
      const slug =
        propertyLabel
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || "report";
      const filename = `${slug}-traffic-snapshot-${new Date().toISOString().slice(0, 10)}.xlsx`;

      // M12.4 — when the property has customConversions configured,
      // surface them as an extra column alongside Conversions on each
      // sheet. The header is omitted when nobody has opted in so
      // pre-M12.4 exports stay byte-identical.
      const hasCustomConversions = report.overview.customConversions !== undefined;
      const overviewRows: Array<[string, string | number]> = [
        ["Sessions", report.overview.sessions],
        ["Engagement rate", `${(report.overview.engagementRate * 100).toFixed(2)}%`],
        // M13.2 — depth metrics on the overview sheet so the headline
        // numbers in the report and the workbook reconcile row-for-row.
        ["Pages / session", Number(report.overview.pagesPerSession.toFixed(2))],
        ["Pageviews", report.overview.pageviews],
        ["Conversions", report.overview.keyEvents],
      ];
      if (hasCustomConversions) {
        overviewRows.push(["Custom conversions", report.overview.customConversions ?? 0]);
      }
      // M13.4 — append ecommerce KPI rows when the section is active.
      // Stays optional so non-ecommerce properties keep the same row
      // count they had pre-M13.4.
      if (
        report.ecommerce?.windowActive &&
        report.overview.transactions !== undefined &&
        report.overview.averageOrderValue !== undefined
      ) {
        overviewRows.push(
          ["Transactions", report.overview.transactions],
          [
            `AOV (${report.meta.currencyCode ?? "currency unset"})`,
            Number(report.overview.averageOrderValue.toFixed(2)),
          ],
        );
      }
      overviewRows.push(
        [`Revenue (${report.meta.currencyCode ?? "USD"})`, report.overview.totalRevenue],
        ["Total users", report.overview.totalUsers],
        ["Period", formatDateRangeLabel(startDate, endDate)],
        ["Property", propertyLabel],
      );

      const platformColumns = [
        "Platform",
        "Sessions",
        "Engaged sessions",
        "Conversions",
        ...(hasCustomConversions ? ["Custom conversions"] : []),
        "Revenue",
        "Top landing page",
      ];
      const platformRows = report.perPlatform.map((p) => {
        const row: Array<string | number> = [
          p.platform,
          p.sessions,
          p.engagedSessions,
          p.keyEvents,
        ];
        if (hasCustomConversions) row.push(p.customConversions ?? 0);
        row.push(p.totalRevenue, p.topLandingPage ?? "");
        return row;
      });

      const landingColumns = [
        "Landing page",
        "Page URL",
        "Top platform",
        "Sessions",
        "Conversions",
        ...(hasCustomConversions ? ["Custom conversions"] : []),
      ];
      const landingRows = report.landingPages.map((r) => {
        const row: Array<string | number> = [
          r.landingPage,
          r.pageUrl ?? "",
          r.platform,
          r.sessions,
          r.keyEvents,
        ];
        if (hasCustomConversions) row.push(r.customConversions ?? 0);
        return row;
      });

      const countryColumns = [
        "Country",
        "Sessions",
        "Conversions",
        ...(hasCustomConversions ? ["Custom conversions"] : []),
        "Revenue",
      ];
      const countryRows = report.perCountry.map((c) => {
        const row: Array<string | number> = [c.country, c.sessions, c.keyEvents];
        if (hasCustomConversions) row.push(c.customConversions ?? 0);
        row.push(c.totalRevenue);
        return row;
      });

      // M13.2 — Depth sheet. Per-date rollup across every platform of
      // pageviews / activeUsers / totalUsers + computed pages-per-session.
      // Mirrors the dashboard table; no granularity bucketing here so
      // analysts get the daily resolution by default and can roll up
      // themselves in Excel.
      const depthAccs = new Map<
        string,
        { pageviews: number; activeUsers: number; totalUsers: number; sessions: number }
      >();
      for (const r of report.trend) {
        const acc = depthAccs.get(r.date) ?? {
          pageviews: 0,
          activeUsers: 0,
          totalUsers: 0,
          sessions: 0,
        };
        acc.pageviews += r.pageviews;
        acc.activeUsers += r.activeUsers;
        acc.totalUsers += r.totalUsers;
        acc.sessions += r.sessions;
        depthAccs.set(r.date, acc);
      }
      const depthSheet =
        depthAccs.size > 0
          ? {
              sheetName: "Depth",
              columns: [
                "Date",
                "Pageviews",
                "Active users",
                "Total users",
                "Sessions",
                "Pages / session",
              ],
              rows: Array.from(depthAccs.entries())
                .sort((a, b) => (a[0] < b[0] ? -1 : 1))
                .map(([date, vals]) => {
                  const pps = vals.sessions > 0 ? vals.pageviews / vals.sessions : 0;
                  return [
                    date,
                    vals.pageviews,
                    vals.activeUsers,
                    vals.totalUsers,
                    vals.sessions,
                    Number(pps.toFixed(2)),
                  ] as Array<string | number>;
                }),
            }
          : null;

      // M13.3 — Mobile sheet. Omitted on legacy share envelopes (and
      // pre-M13.3 cached payloads) that don't carry the block. Per-device
      // summary on top of the sheet; daily trend rows for analysts who
      // want to chart the day-over-day shift in Excel.
      const mobileSheet = report.mobile
        ? {
            sheetName: "Mobile",
            columns: [
              "Device",
              "Sessions",
              "Share %",
              "Active users",
              "Total users",
              "Key events",
              "Conv rate %",
              "Revenue",
            ],
            rows: (() => {
              const total = report.mobile!.perDevice.reduce((acc, r) => acc + r.sessions, 0);
              const summary = report.mobile!.perDevice.map((r) => {
                const share = total > 0 ? (r.sessions / total) * 100 : 0;
                return [
                  r.device,
                  r.sessions,
                  Number(share.toFixed(2)),
                  r.activeUsers,
                  r.totalUsers,
                  r.keyEvents,
                  Number((r.sessionKeyEventRate * 100).toFixed(2)),
                  r.totalRevenue,
                ] as Array<string | number>;
              });
              // Blank row + trend section header so the per-day rows
              // don't visually merge with the per-device summary above.
              const trendHeader: Array<string | number> = [
                "— Daily trend —",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
              ];
              const trendRows = report.mobile!.trend.map(
                (r) =>
                  [
                    `${r.date} · ${r.device}`,
                    r.sessions,
                    "",
                    r.activeUsers,
                    r.totalUsers,
                    "",
                    "",
                    "",
                  ] as Array<string | number>,
              );
              return [...summary, ["", "", "", "", "", "", "", ""], trendHeader, ...trendRows];
            })(),
          }
        : null;

      // M13.4 — Ecommerce sheet. Two sections in one tab: per-channel
      // rows on top, per-platform rows below (separated by a blank
      // row + sub-header so analysts pivot the two cleanly). Omitted
      // when ecommerce is null OR windowActive is false. Currency
      // values stay numeric — Excel formats them per the locale.
      const ecommerceSheet =
        report.ecommerce && report.ecommerce.windowActive
          ? {
              sheetName: "Ecommerce",
              columns: [
                "Bucket",
                "Revenue",
                "Transactions",
                "AOV",
                "Revenue per user",
                "Conv rate %",
              ],
              rows: (() => {
                const channelRows = report.ecommerce!.perChannel.map(
                  (r) =>
                    [
                      r.channel,
                      Number(r.revenue.toFixed(2)),
                      r.purchases,
                      Number(r.averageOrderValue.toFixed(2)),
                      Number(r.revenuePerUser.toFixed(2)),
                      Number((r.purchaserConversionRate * 100).toFixed(2)),
                    ] as Array<string | number>,
                );
                const platformRows = report.ecommerce!.perPlatform.map(
                  (r) =>
                    [
                      r.platform,
                      Number(r.revenue.toFixed(2)),
                      r.purchases,
                      Number(r.averageOrderValue.toFixed(2)),
                      Number(r.revenuePerUser.toFixed(2)),
                      Number((r.purchaserConversionRate * 100).toFixed(2)),
                    ] as Array<string | number>,
                );
                const channelHeader: Array<string | number> = [
                  "— Per channel —",
                  "",
                  "",
                  "",
                  "",
                  "",
                ];
                const platformHeader: Array<string | number> = [
                  "— Per platform —",
                  "",
                  "",
                  "",
                  "",
                  "",
                ];
                const blank: Array<string | number> = ["", "", "", "", "", ""];
                const currencyNote: Array<string | number> = [
                  `Currency: ${report.meta.currencyCode ?? "(unresolved)"}`,
                  "",
                  "",
                  "",
                  "",
                  "",
                ];
                return [
                  currencyNote,
                  blank,
                  channelHeader,
                  ...channelRows,
                  blank,
                  platformHeader,
                  ...platformRows,
                ];
              })(),
            }
          : null;

      // M13.1 — Source mix sheet. Omitted on legacy share envelopes
      // (and pre-M13.1 cached payloads) that don't carry the block. Each
      // row is one of the 15 GA4 channel-group buckets verbatim — no
      // Other rollup here so analysts can pivot freely in Excel.
      const sourceMixSheet = report.sourceMix
        ? {
            sheetName: "Source mix",
            columns: [
              "Channel",
              "Sessions",
              "Share %",
              "Sessions Δ vs compare %",
              "Active users",
              "Total users",
              "Pageviews",
            ],
            rows: report.sourceMix.perChannel.map((r) => {
              const totalSessions = report.sourceMix!.perChannel.reduce(
                (acc, x) => acc + x.sessions,
                0,
              );
              const share = totalSessions > 0 ? (r.sessions / totalSessions) * 100 : 0;
              const deltaPct =
                r.deltas.sessions != null && Number.isFinite(r.deltas.sessions)
                  ? r.deltas.sessions * 100
                  : "";
              return [
                r.channel,
                r.sessions,
                Number(share.toFixed(2)),
                deltaPct === "" ? "" : Number((deltaPct as number).toFixed(2)),
                r.activeUsers,
                r.totalUsers,
                r.pageviews,
              ] as Array<string | number>;
            }),
          }
        : null;

      const sheets = [
        {
          sheetName: "Traffic report",
          columns: ["Metric", "Value"],
          rows: overviewRows,
        },
        {
          sheetName: "Platforms",
          columns: platformColumns,
          rows: platformRows,
        },
        {
          sheetName: "Landing pages",
          columns: landingColumns,
          rows: landingRows,
        },
        {
          sheetName: "Countries",
          columns: countryColumns,
          rows: countryRows,
        },
        ...(sourceMixSheet ? [sourceMixSheet] : []),
        ...(depthSheet ? [depthSheet] : []),
        ...(mobileSheet ? [mobileSheet] : []),
        ...(ecommerceSheet ? [ecommerceSheet] : []),
        {
          sheetName: "UTM Issues",
          columns: ["Category", "Severity", "Label", "Rows", "Sessions"],
          rows: report.utmHealth.issues.map((i) => [
            i.category,
            i.severity,
            i.label,
            i.rowCount,
            i.sessionCount,
          ]),
        },
        {
          sheetName: "Recommendations",
          columns: ["Category", "Severity", "Message"],
          rows: report.recommendations.map((r) => [r.category, r.severity, r.message]),
        },
      ];

      await downloadXlsx({
        fileName: filename,
        sheets,
      });
      pushGtmEvent("export_xlsx_downloaded", {
        property_id: report.meta.propertyId,
        range: report.meta.range,
        compare: report.meta.compare,
        start_date: startDate,
        end_date: endDate,
        sheet_count: sheets.length,
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <ExportCardShell
      icon="fa-regular fa-file-excel"
      title="Data bundle"
      description="Every section of the report as a six-tab Excel workbook. Downloads instantly — no email."
    >
      <Button type="button" onClick={download} disabled={downloading} className="ts-cta">
        {downloading ? "Preparing…" : "Download XLSX"}
        <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
      </Button>
    </ExportCardShell>
  );
}

function ComingSoonCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: string;
}) {
  return (
    <ExportCardShell tone="muted" icon={icon} title={title} description={description}>
      <span
        aria-disabled="true"
        className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-neutral-400 ring-1 ring-neutral-200"
      >
        Soon
      </span>
    </ExportCardShell>
  );
}
